/**
 * THE single source of truth for the tenant-site domain (`<org>.<root>`).
 *
 * Migration context (2026-08): tenant org sites are moving from
 * `<org>.staty.io` to `<org>.statyapps.io` so tenant content and tenant abuse
 * risk stop sharing a registrable domain with the platform (marketing site,
 * dash, api). `TENANT_ROOT_DOMAIN` is what every URL builder emits — flipping
 * it IS the canonical cutover. `LEGACY_TENANT_ROOT_DOMAIN` must keep parsing
 * forever: old links 301 through the Cloudflare worker for as long as they
 * exist in the wild.
 *
 * Rules:
 *  - NEVER hardcode `staty.io`/`statyapps.io` in tenant URL construction or
 *    host parsing — import from here. The deployed tenant worker does
 *    (apps/next-app/worker.ts → utils/tenantEdge.ts); apps/app/worker.js, the
 *    frozen rollback target, still carries the pre-extraction mirrored copy.
 *  - Platform hosts (dash./api./docs./www./marketing apex) are NOT tenant
 *    hosts and permanently stay on staty.io — they don't follow
 *    TENANT_ROOT_DOMAIN.
 */

// What new tenant URLs are built with. PHASE 2 FLIPPED 2026-08-26: every
// emitted tenant URL (emails, Stripe redirects, admin links, public app) now
// uses statyapps.io. The worker 301s legacy-root tenant hosts here.
export const TENANT_ROOT_DOMAIN = 'statyapps.io';

// The pre-migration root. Parsed + 301-redirected forever; never emitted.
export const LEGACY_TENANT_ROOT_DOMAIN = 'staty.io';

// Platform surfaces that never move.
export const ADMIN_ORIGIN = 'https://dash.staty.io';
export const API_ORIGIN = 'https://api.staty.io';

// Subdomain labels on either root that are platform surfaces, not tenants.
// Consumed by utils/tenantEdge.ts (the worker); mirrored inline in the frozen
// apps/app/worker.js (NON_TENANT_SUBDOMAINS).
// 'tenants' is the Cloudflare-for-SaaS fallback origin for custom domains
// (INT-0007) — customers CNAME to it; it must never resolve as an org slug.
export const NON_TENANT_SUBDOMAINS = [
  'www',
  'api',
  'dash',
  'docs',
  'admin',
  'try',
  'staty',
  'login',
  'signup',
  'tenants',
];

// Where customers point their CNAME for a custom domain (INT-0007). This is
// the Cloudflare-for-SaaS fallback origin on the statyapps.io zone; it routes
// to the tenant worker (staty-next-app, ADR 0066) like any tenant host.
export const CUSTOM_DOMAIN_CNAME_TARGET = `tenants.${TENANT_ROOT_DOMAIN}`;

/** Production origin for a tenant site: `https://<slug>.<TENANT_ROOT_DOMAIN>`. */
export function tenantOrigin(slug: string): string {
  return `https://${String(slug || '').toLowerCase()}.${TENANT_ROOT_DOMAIN}`;
}

/**
 * Local-dev origin for a tenant site. With a slug: `http://<slug>.localhost:3000`
 * (the Vite dev server accepts any *.localhost host and getLeagueName parses the
 * first label). Without: bare `http://localhost:3000` (server-built links where
 * the local app falls back to `?leagueName=`/DEFAULT_LEAGUE).
 */
export function localTenantOrigin(slug?: string): string {
  return slug
    ? `http://${String(slug).toLowerCase()}.localhost:3000`
    : 'http://localhost:3000';
}

/**
 * The org's canonical public origin: its ACTIVE custom domain when one exists,
 * else the tenant subdomain (INT-0007). THE one org-aware origin builder —
 * every emitter with an org document in hand should use this instead of
 * `tenantOrigin(org.name)` so links flip to the custom domain automatically.
 * Accepts a plain org doc (or anything with `name` + optional `customDomain`).
 */
export function orgOrigin(org: {
  name?: string;
  customDomain?: { hostname?: string; status?: string } | null;
}): string {
  const cd = org && org.customDomain;
  if (cd && cd.status === 'active' && cd.hostname) {
    return `https://${String(cd.hostname).toLowerCase()}`;
  }
  return tenantOrigin((org && org.name) || '');
}

// Conservative hostname shape: labels of letters/digits/hyphens, at least one
// dot, a 2+ letter TLD, no leading/trailing hyphens. Deliberately rejects
// underscores, IPs, ports, and trailing dots.
const HOSTNAME_RE =
  /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/**
 * Can this hostname be claimed as a custom domain (INT-0007)? Returns null
 * when claimable, else a human-readable refusal. Platform roots and anything
 * under either tenant root can never be claimed — a claim of
 * `foo.statyapps.io` would collide with tenant serving, and `api.staty.io`
 * would be catastrophic. Cross-ORG uniqueness is a DB concern, not checked
 * here.
 */
export function customDomainRefusal(hostname: string): string | null {
  const h = String(hostname || '').trim().toLowerCase();
  if (!h) return 'Enter a domain name.';
  if (!HOSTNAME_RE.test(h)) {
    return 'That does not look like a valid domain name (e.g. leagues.example.com).';
  }
  for (const root of [TENANT_ROOT_DOMAIN, LEGACY_TENANT_ROOT_DOMAIN]) {
    if (h === root || h.endsWith(`.${root}`)) {
      return 'Staty domains cannot be claimed as custom domains.';
    }
  }
  return null;
}

/** True when the hostname is a bare apex (example.com — no subdomain label). */
export function isApexDomain(hostname: string): boolean {
  return String(hostname || '').trim().toLowerCase().split('.').length === 2;
}

/**
 * Parse a request/browser host into a tenant slug, or null if the host is not
 * a tenant site (platform subdomain, bare apex, unknown domain). Accepts the
 * current root, the legacy root (parsed forever — old links in the wild), and
 * `<slug>.localhost[:port]` for dev. A CUSTOM domain is NOT resolvable here —
 * it needs the byDomain lookup (worker/api); this stays the static-roots
 * parser.
 */
export function isTenantHost(host: string): string | null {
  if (!host) return null;
  const hostname = String(host).toLowerCase().split(':')[0];

  for (const root of [
    TENANT_ROOT_DOMAIN,
    LEGACY_TENANT_ROOT_DOMAIN,
    'localhost',
  ]) {
    if (hostname === root) return null; // bare apex — not a tenant
    if (hostname.endsWith(`.${root}`)) {
      const sub = hostname.slice(0, -(root.length + 1));
      if (sub.includes('.')) return null; // nested labels aren't tenants
      if (NON_TENANT_SUBDOMAINS.includes(sub)) return null;
      return sub || null;
    }
  }
  return null;
}
